/**
 * audio/spatial.js — cheap positional audio.
 *
 * GDD 11.1 asks for "you hear the duck gallery to your left", not for HRTF.
 * A full PannerNode per voice would cost far more than that goal is worth on a
 * Quest 3S, so sounds carrying `opts.position` get stereo pan plus distance
 * attenuation computed once at schedule time, and the listener pose is only
 * re-read at 10 Hz.
 *
 * The listener transform is read straight off `object3D.matrixWorld.elements`
 * rather than importing three.js — the audio layer stays dependency-free, and
 * a 16-float array is all the maths needs.
 *
 * @module audio/spatial
 */

import { EVENTS, STALL_BY_ID, stallPlayOrigin } from '../constants.js';
import { bus } from '../core/events.js';
import { onTick } from './context.js';
import { arand } from './synth.js';

/** Full volume within this radius. */
const REF_DISTANCE = 2.5;
/** Silent beyond this. */
const MAX_DISTANCE = 34;
const ROLLOFF = 0.55;
/** Listener pose refresh period. */
const LISTENER_HZ = 10;
/** Metres of travel per footfall (GDD 11.2 "goose waddle"). */
const STRIDE = 0.62;
/** A frame moving further than this is a teleport, not a step. */
const TELEPORT_JUMP = 0.5;

const state = {
  /** @type {object|null} */
  listener: null,
  pos: [0, 1.6, 0],
  right: [1, 0, 0],
  forward: [0, 0, -1],
  lastUpdate: 0,
  installed: false,
  stepAccum: 0,
  stepLast: null,
  /** Footsteps stay suppressed while this is positive (post-teleport). */
  stepMute: 0,
  /** @type {Map<string, object>} */
  emitters: new Map(),
  emittersEnabled: true,
  /** @type {((name: string, opts: object) => void)|null} */
  play: null,
};

/**
 * Ambience each stall radiates into the midway. Deliberately sparse and quiet —
 * this is background texture, not a second copy of the game.
 */
const STALL_EMITTERS = Object.freeze({
  'tin-can-alley': { name: 'can-clatter', gain: 0.22, every: [7, 15] },
  'coconut-shy': { name: 'coconut-thunk', gain: 0.24, every: [8, 17] },
  'ring-toss': { name: 'rim-clang', gain: 0.16, every: [9, 19] },
  'ping-pong-goldfish': { name: 'splash', gain: 0.18, every: [8, 16] },
  'basketball-shoot': { name: 'rim-clang', gain: 0.18, every: [8, 16] },
  'skee-ball': { name: 'plate-knock', gain: 0.20, every: [6, 13] },
  'duck-gallery': { name: 'machine-hum', gain: 0.14, every: [10, 16] },
  'dart-balloons': { name: 'balloon-pop', gain: 0.20, every: [7, 15] },
  'high-striker': { name: 'bell-ring', gain: 0.10, every: [22, 45] },
  'whack-a-mole': { name: 'mole-bonk', gain: 0.20, every: [5, 11] },
  'hook-a-duck': { name: 'bubbles', gain: 0.16, every: [7, 14] },
  'coin-pusher': { name: 'machine-hum', gain: 0.16, every: [9, 15] },
});

// ─────────────────────────────────────────────────────────────────────────────
// Listener
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The object whose world transform is the ear position — normally the camera.
 * @param {object|null} object3D
 */
export function setListener(object3D) {
  state.listener = object3D || null;
  readListener();
}

/** @private The listener's transform, or null if it is not ready yet. */
function matrix() {
  const o = state.listener;
  const e = o && o.matrixWorld && o.matrixWorld.elements;
  return e && e.length >= 16 ? e : null;
}

/** @private Position only — cheap enough to run every frame. */
function readPosition() {
  const e = matrix();
  if (e) state.pos = [e[12], e[13], e[14]];
}

/** @private Pull position and basis vectors out of the listener's matrix. */
function readListener() {
  const e = matrix();
  if (!e) return;
  state.pos = [e[12], e[13], e[14]];
  state.right = norm([e[0], e[1], e[2]]);
  // A three.js camera looks down its local −Z.
  state.forward = norm([-e[8], -e[9], -e[10]]);
}

/**
 * Fire a footstep every `STRIDE` metres travelled.
 *
 * Deriving steps from distance rather than from the avatar's waddle hook keeps
 * the audio layer from importing `avatar/`, and it self-corrects for speed: a
 * faster waddle simply covers the stride sooner. Teleports are excluded by the
 * per-frame jump threshold, and for a moment afterwards, because the follow
 * camera lerps into place over several frames and would otherwise walk there.
 *
 * @private
 * @param {number} dt
 */
function stepTick(dt) {
  if (state.stepMute > 0) state.stepMute -= dt;

  const p = state.pos;
  const last = state.stepLast;
  state.stepLast = [p[0], p[1], p[2]];
  if (!last || !state.play) return;

  const d = Math.hypot(p[0] - last[0], p[2] - last[2]);   // horizontal only
  if (d >= TELEPORT_JUMP) { state.stepMute = 0.6; state.stepAccum = 0; return; }
  if (state.stepMute > 0 || d < 0.0005) return;

  state.stepAccum += d;
  if (state.stepAccum < STRIDE) return;
  state.stepAccum -= STRIDE;
  state.play('waddle-step', {});
}

/** @returns {[number, number, number]} */
export function listenerPosition() {
  return [...state.pos];
}

// ─────────────────────────────────────────────────────────────────────────────
// Resolution
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Turn a world position into pan and gain relative to the listener.
 * @param {number[]|{x:number,y:number,z:number}} position
 * @returns {{ pan: number, gain: number, distance: number }}
 */
export function resolve(position) {
  const p = toArray(position);
  if (!p) return { pan: 0, gain: 1, distance: 0 };

  const dx = p[0] - state.pos[0];
  const dy = p[1] - state.pos[1];
  const dz = p[2] - state.pos[2];
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz);

  if (d >= MAX_DISTANCE) return { pan: 0, gain: 0, distance: d };

  // Inverse-distance rolloff, flat inside the reference radius.
  let gain = d <= REF_DISTANCE
    ? 1
    : REF_DISTANCE / (REF_DISTANCE + ROLLOFF * (d - REF_DISTANCE));
  // Fade the last quarter to nothing so sounds do not cut off abruptly.
  const fadeFrom = MAX_DISTANCE * 0.75;
  if (d > fadeFrom) gain *= 1 - (d - fadeFrom) / (MAX_DISTANCE - fadeFrom);

  const inv = 1 / Math.max(0.001, d);
  const lateral = (dx * state.right[0] + dy * state.right[1] + dz * state.right[2]) * inv;
  const front = (dx * state.forward[0] + dy * state.forward[1] + dz * state.forward[2]) * inv;
  // Behind the listener reads quieter — the cheap stand-in for head shadowing.
  if (front < 0) gain *= 1 + front * 0.18;

  return {
    pan: Math.max(-1, Math.min(1, lateral)) * 0.92,
    gain: Math.max(0, Math.min(1, gain)),
    distance: d,
  };
}

/**
 * Merge spatial pan/gain into a play options object.
 * @param {object} opts
 * @returns {object} A copy; `null` gain means inaudible.
 */
export function apply(opts = {}) {
  if (!opts.position) return opts;
  const { pan, gain, distance } = resolve(opts.position);
  return {
    ...opts,
    pan: opts.pan != null ? opts.pan : pan,
    volume: (opts.volume != null ? opts.volume : 1) * gain,
    distance,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Stall emitters
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Register an ambience emitter.
 * @param {string} id
 * @param {{ position: number[], name: string, gain?: number, every?: number[] }} spec
 */
export function addEmitter(id, spec) {
  const every = spec.every || [8, 16];
  state.emitters.set(id, {
    id,
    position: toArray(spec.position) || [0, 0, 0],
    name: spec.name,
    gain: spec.gain != null ? spec.gain : 0.2,
    every,
    // Stagger first fires so twelve booths do not all speak at once.
    next: 2 + arand() * every[1],
  });
}

/** @param {string} id */
export function removeEmitter(id) {
  state.emitters.delete(id);
}

/** Drop every emitter. */
export function clearEmitters() {
  state.emitters.clear();
}

/** @returns {string[]} */
export function emitterIds() {
  return [...state.emitters.keys()];
}

/**
 * Emitters go quiet inside a stall — the player is in a booth, not the midway.
 * @param {boolean} enabled
 */
export function setEmittersEnabled(enabled) {
  state.emittersEnabled = !!enabled;
}

/**
 * Advance emitter timers. Driven by the shared audio scheduler.
 * @param {number} dt Seconds since the last call.
 */
export function tickEmitters(dt) {
  if (!state.emittersEnabled || !state.play) return;
  for (const em of state.emitters.values()) {
    em.next -= dt;
    if (em.next > 0) continue;
    em.next = em.every[0] + arand() * (em.every[1] - em.every[0]);

    const { pan, gain } = resolve(em.position);
    if (gain <= 0.02) continue;      // out of earshot; skip the work entirely
    state.play(em.name, {
      pan,
      volume: gain * em.gain,
      duration: 1.2,
      _spatial: true,
    });
  }
}

/**
 * Create the emitter for a stall from its GDD hub slot.
 * @param {string} stallId
 * @returns {boolean} False if the stall has no emitter defined.
 */
export function addStallEmitter(stallId) {
  const spec = STALL_BY_ID[stallId];
  const em = STALL_EMITTERS[stallId];
  if (!spec || !em) return false;
  const origin = stallPlayOrigin(spec);
  // Push it into the booth so it pans as coming from the stall, not the aisle.
  const sign = spec.row === 'left' ? -1 : 1;
  addEmitter(stallId, {
    position: [origin.x + sign * 2.4, 1.4, origin.z],
    name: em.name,
    gain: em.gain,
    every: em.every,
  });
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Wiring
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {{ play: (name: string, opts: object) => void }} deps
 */
export function install({ play }) {
  state.play = play;
  if (state.installed) return;
  state.installed = true;

  const period = 1 / LISTENER_HZ;
  bus.on(EVENTS.FRAME, ({ dt, elapsed }) => {
    // Position every frame — footstep timing needs the resolution, and it is
    // three array reads. The basis vectors only pan sounds, so 10 Hz is plenty.
    readPosition();
    stepTick(dt || 0);
    if (elapsed - state.lastUpdate < period) return;
    state.lastUpdate = elapsed;
    readListener();
  });

  // A blink is not a walk.
  bus.on(EVENTS.TELEPORT, () => { state.stepMute = 0.6; state.stepAccum = 0; });

  // Emitter timers ride the shared audio scheduler rather than the frame loop:
  // it only runs once a context exists, which is exactly when they can be heard.
  let lastTick = 0;
  onTick((now) => {
    const dt = lastTick ? Math.max(0, now - lastTick) : 0;
    lastTick = now;
    if (dt > 0) tickEmitters(dt);
  });

  // Stalls call registerStall() at module scope, so STALL_REGISTERED has
  // already fired for all twelve by the time audio installs — subscribing
  // alone would leave the midway silent. Seed every booth up front and keep the
  // subscription for anything registered later.
  //
  // Every booth gets an emitter, implemented or not: the hub builds twelve
  // stalls regardless, and a COMING SOON pitch that is audibly part of a busy
  // carnival serves GDD 11.1 better than a hole in the soundscape.
  for (const id of Object.keys(STALL_BY_ID)) addStallEmitter(id);
  bus.on(EVENTS.STALL_REGISTERED, ({ id } = {}) => {
    if (id) addStallEmitter(id);
  });

  bus.on(EVENTS.AVATAR_MODE, ({ mode } = {}) => {
    setEmittersEnabled(mode !== 'first');
  });
}

/** Diagnostic snapshot. */
export function snapshot() {
  return {
    listener: state.pos,
    // The basis is exposed so a test can check panning against where the
    // listener is actually facing rather than assuming a world axis.
    right: [...state.right],
    forward: [...state.forward],
    emitters: emitterIds(),
    emittersEnabled: state.emittersEnabled,
    stepAccum: state.stepAccum,
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/** @param {any} p @returns {number[]|null} */
function toArray(p) {
  if (!p) return null;
  if (Array.isArray(p)) return p.length >= 3 ? p : null;
  if (typeof p.x === 'number') return [p.x, p.y, p.z];
  return null;
}

/** @param {number[]} v @returns {number[]} */
function norm(v) {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}
